// @ts-nocheck
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  NJHeading, NJSpinner, NJInlineMessage, NJBadge, NJText, NJToastContainer,
  NJIconButton, NJIcon, NJTooltip,
  NJTabs, NJTab,
  NJSidebarBrandDeprecated, NJSidebarCloseDeprecated, NJSidebarDeprecated,
  NJSidebarDividerDeprecated, NJSidebarMenuDeprecated,
} from '@engie-group/fluid-design-system-react';
import { PrototypeStudioLayout } from '@shared';
import { useExplorerData } from './hooks';
import DspListView from './DspListView';
import StatsTab from './StatsTab';
import type { Selection, DSP, City, Department, Network } from './types';

// ── Sidebar constants (Pattern 4a) ─────────────────────────────────────────
const PRIMARY_SIDEBAR_WIDTH_OPEN   = 230;
const PRIMARY_SIDEBAR_WIDTH_FOLDED = 60;

const PRIMARY_NAV = [
  { id: 'networks',     icon: 'account_tree',  label: 'Réseaux',     active: true },
  { id: 'calculations', icon: 'legend_toggle', label: 'Calculs',     active: false },
  { id: 'coreai',       icon: 'engie_ai',      label: 'Nemo CoreAI', active: false, ai: true },
];
const PRIMARY_FOOTER = [
  { id: 'help',    icon: 'help_outline',       label: 'Guide utilisateur' },
  { id: 'profile', icon: 'account_circle',     label: 'Profil' },
  { id: 'logout',  icon: 'power_settings_new', label: 'Déconnexion' },
];

const css = `
  .nemo-primary-sidebar {
    position: relative !important; top: 0 !important; left: 0 !important;
    width: 100% !important; height: 100% !important; min-width: 0 !important;
  }
  .hidden { display: none !important; }
  .nemo-primary-sidebar .nj-list-deprecated__item--clickable,
  .nemo-primary-sidebar .nj-list-deprecated__item--active {
    border-right: none !important; box-shadow: none !important;
  }
  .nemo-primary-sidebar .nj-list-deprecated__item--clickable:hover {
    box-shadow: inset -3px 0 0 var(--nj-semantic-color-border-brand-primary-default) !important;
  }
  .nemo-primary-sidebar .nj-list-deprecated__item button {
    display: flex !important; align-items: center !important;
    justify-content: center !important; width: 100% !important; gap: 16px;
  }
  .leaflet-tooltip.nemo-map-tooltip {
    background: #fff !important; border: 1px solid #e0e0e0 !important;
    border-radius: 8px !important; box-shadow: 0 4px 16px rgba(0,0,0,.14) !important;
    padding: 10px 14px !important; font-family: 'Lato', sans-serif !important;
    font-size: 13px !important; color: #212121 !important;
    white-space: normal !important; max-width: 260px !important;
  }
  .leaflet-tooltip.nemo-map-tooltip::before { display: none !important; }
`;

export const meta = {
  title: 'Gestionnaire Réseaux — Vue Liste',
  description: 'Table de DSP avec carte et filtre automatique par zoom',
  tags: ['list', 'network', 'po'],
  author: 'erika.dal-monte',
  status: 'draft' as const,
  category: 'network-list',
};

const CITY_COORDS: Record<string, [number, number]> = {
'Paris': [48.857, 2.352],
  'Versailles': [48.805, 2.135],
  'Évry': [48.626, 2.428],
  'Nanterre': [48.892, 2.207],
  'Bobigny': [48.907, 2.44],
  'Créteil': [48.774, 2.456],
  'Cergy': [49.036, 2.073],
  'Meaux': [48.96, 2.887],
  'Lille': [50.629, 3.057],
  'Strasbourg': [48.573, 7.752],
  'Metz': [49.119, 6.176],
  'Nancy': [48.692, 6.184],
  'Reims': [49.258, 4.032],
  'Amiens': [49.895, 2.302],
  'Besançon': [47.237, 6.024],
  'Dijon': [47.322, 5.041],
  'Mulhouse': [47.75, 7.339],
  'Charleville-Mézières': [49.771, 4.717],
  'Nantes': [47.218, -1.554],
  'Rennes': [48.114, -1.68],
  'Tours': [47.394, 0.686],
  'Orléans': [47.903, 1.909],
  'Le Mans': [47.995, 0.192],
  'Brest': [48.39, -4.486],
  'Caen': [49.183, -0.353],
  'Rouen': [49.443, 1.1],
  'Angers': [47.473, -0.553],
  'La Rochelle': [46.16, -1.152],
  'Bordeaux': [44.837, -0.579],
  'Toulouse': [43.605, 1.444],
  'Pau': [43.296, -0.37],
  'Limoges': [45.835, 1.262],
  'Périgueux': [45.184, 0.72],
  'Agen': [44.202, 0.62],
  'Albi': [43.929, 2.149],
  'Bayonne': [43.493, -1.475],
  'Lyon': [45.764, 4.836],
  'Grenoble': [45.188, 5.724],
  'Marseille': [43.296, 5.381],
  'Nice': [43.71, 7.262],
  'Montpellier': [43.611, 3.877],
  'Clermont-Ferrand': [45.778, 3.087],
  'Valence': [44.926, 4.893],
  'Toulon': [43.124, 5.927],
  'Avignon': [43.949, 4.806],
  'Chambéry': [45.564, 5.92],
  'Annecy': [45.9, 6.117],
  'Saint-Étienne': [45.435, 4.387],
  'Madrid': [40.417, -3.703],
  'Barcelona': [41.385, 2.173],
  'Valencia': [39.47, -0.376],
  'Sevilla': [37.389, -5.984],
  'Zaragoza': [41.649, -0.887],
  'Bilbao': [43.263, -2.935],
  'Valladolid': [41.654, -4.724],
  'Vitoria-Gasteiz': [42.85, -2.673],
  'San Sebastián': [43.318, -1.982],
  'Burgos': [42.344, -3.696],
  'Móstoles': [40.323, -3.865],
  'Teruel': [40.343, -1.106],
  'Milano': [45.465, 9.188],
  'Torino': [45.071, 7.687],
  'Roma': [41.894, 12.482],
  'Bologna': [44.494, 11.342],
  'Firenze': [43.77, 11.256],
  'Genova': [44.408, 8.934],
  'Brescia': [45.54, 10.219],
  'Bergamo': [45.695, 9.67],
  'Verona': [45.439, 10.993],
  'Padova': [45.407, 11.876],
};
const TYPE_COLOR: Record<string, string> = {
  'hot water': '#F34E46', 'cold water': '#007BC5', steam: '#e65100', condensate: '#6a1b9a',
};
const TYPE_LABEL: Record<string, string> = {
  'hot water': 'Eau chaude', 'cold water': 'Eau froide', steam: 'Vapeur', condensate: 'Condensats',
};

const TERRITORY_STYLE: Record<string, { fill: string; border: string }> = {
  // France
  idf: { fill: '#e8d5f5', border: '#9c6bc7' }, // lavender
  co:  { fill: '#b0c8e8', border: '#4a7db0' }, // steel blue
  so:  { fill: '#ffd8a8', border: '#d08030' }, // amber
  se:  { fill: '#b8e4b0', border: '#4a9840' }, // green
  ne:  { fill: '#a8d8f0', border: '#2880b0' }, // sky blue
  // International
  es:  { fill: '#f5c8a0', border: '#c06830' }, // orange — Spain
  it:  { fill: '#a8dfc8', border: '#3a9870' }, // teal — Italy
  de:  { fill: '#d0d0e0', border: '#7878a0' }, // grey — Germany
  pl:  { fill: '#f5a8b8', border: '#c04060' }, // rose — Poland
  pt:  { fill: '#b8d8a0', border: '#5a9040' }, // olive — Portugal
  uae: { fill: '#f5e0a0', border: '#c0a030' }, // gold — UAE
};
const FRENCH_TERRITORIES = new Set(['idf', 'co', 'so', 'se', 'ne']);
const TERRITORY_COUNTRY_NAME: Record<string, string> = {
  es: 'Spain', it: 'Italy', de: 'Germany', pl: 'Poland', pt: 'Portugal', uae: 'United Arab Emirates',
};

// ── Map component ──────────────────────────────────────────────────────────────
interface DspScenarioCounts { active: number; archived: number; reference: number; studiesActive: number; studiesArchived: number; }

interface MapProps {
  dsps: DSP[];
  networks: Network[];
  cityById: Record<string, City>;
  selectedDspId: string | null;
  selectedNetworkId: string | null;
  onSelectDsp: (id: string) => void;
  onSelectNetwork: (id: string) => void;
  territoryFilter: string;
  departments: Department[];
  onTerritoryChange: (id: string) => void;
  onBoundsChange: (bounds: { north: number; south: number; east: number; west: number }) => void;
  onZoomChange?: (zoom: number) => void;
  dspScenarioCounts?: Record<string, DspScenarioCounts>;
  showContracts: boolean;
  showNetworks: boolean;
}

function NetworkMap({ dsps, networks, cityById, selectedDspId, selectedNetworkId, onSelectDsp, onSelectNetwork, territoryFilter, departments, onTerritoryChange, onBoundsChange, onZoomChange, dspScenarioCounts, showContracts, showNetworks }: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<unknown>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contractCirclesRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const networkCirclesRef = useRef<any[]>([]);
  const geoLayerRef = useRef<unknown>(null);
  const bgLayerRef = useRef<unknown>(null);
  const didAutoFitNetworksRef = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [franceGeoJson, setFranceGeoJson] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [worldGeoJson, setWorldGeoJson] = useState<any>(null);
  const onSelectRef = useRef(onSelectDsp);
  const onSelectNetworkRef = useRef(onSelectNetwork);
  const onTerritoryChangeRef = useRef(onTerritoryChange);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onZoomChangeRef = useRef(onZoomChange);
  const currentTerritoryRef = useRef(territoryFilter);
  useEffect(() => { onSelectRef.current = onSelectDsp; }, [onSelectDsp]);
  useEffect(() => { onSelectNetworkRef.current = onSelectNetwork; }, [onSelectNetwork]);
  useEffect(() => { onTerritoryChangeRef.current = onTerritoryChange; }, [onTerritoryChange]);
  useEffect(() => { onBoundsChangeRef.current = onBoundsChange; }, [onBoundsChange]);
  useEffect(() => { onZoomChangeRef.current = onZoomChange; }, [onZoomChange]);
  useEffect(() => { currentTerritoryRef.current = territoryFilter; }, [territoryFilter]);

  // dept code → territoryId (stable — departments never change in mock)
  const deptToTerritory = useMemo(
    () => Object.fromEntries(departments.map(d => [d.code, d.territoryId])),
    [departments]
  );
  // country name → territoryId
  const countryToTerritory = useMemo(
    () => Object.fromEntries(Object.entries(TERRITORY_COUNTRY_NAME).map(([tid, name]) => [name, tid])),
    []
  );

  const [ready, setReady] = useState<boolean>(!!(window as { L?: unknown }).L);

  // Load Leaflet from CDN
  useEffect(() => {
    if ((window as { L?: unknown }).L) { setReady(true); return; }
    if (document.querySelector('script[data-leaflet-v2]')) {
      const poll = setInterval(() => { if ((window as { L?: unknown }).L) { setReady(true); clearInterval(poll); } }, 100);
      return () => clearInterval(poll);
    }
    const link = document.createElement('link'); link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.setAttribute('data-leaflet-v2', '1');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, []);

  // Load France departments GeoJSON once
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements-version-simplifiee.geojson')
      .then(r => r.json()).then(d => setFranceGeoJson(d)).catch(() => {});
  }, []);

  // Load world countries GeoJSON once (simplified Natural Earth 110m)
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson')
      .then(r => r.json()).then(d => setWorldGeoJson(d)).catch(() => {});
  }, []);

  // Init map once
  useEffect(() => {
    if (!ready || !containerRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    const map = L.map(containerRef.current, { center: [46.8, 2.5], zoom: 5 });
    mapRef.current = map;
    // Custom panes below the overlay pane (400) so territory fills never cover circles
    map.createPane('territory-bg-pane').style.zIndex = '290'; // clickable hit-areas
    map.createPane('territory-pane').style.zIndex = '300';   // selected highlight
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 19,
    }).addTo(map);
    // Fire initial bounds + on every pan/zoom
    const fireBounds = () => {
      const b = map.getBounds();
      onBoundsChangeRef.current({ north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() });
      onZoomChangeRef.current?.(map.getZoom());
    };
    map.on('moveend', fireBounds);
    // Fire once map is ready
    map.whenReady(fireBounds);
    return () => {
      map.remove();
      mapRef.current = null;
      contractCirclesRef.current = [];
      networkCirclesRef.current = [];
      geoLayerRef.current = null;
      bgLayerRef.current = null;
    };
  }, [ready]);

  // Render background territories (always visible, clickable — click → set filter)
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;
    if (bgLayerRef.current) { (bgLayerRef.current as any).remove(); bgLayerRef.current = null; }
    if (!franceGeoJson && !worldGeoJson) return;
    const bgStyle = { fillColor: '#fff', fillOpacity: 0.001, color: '#bbb', weight: 0.5, opacity: 0.4, pane: 'territory-bg-pane' };
    const layers = [];
    if (franceGeoJson) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      layers.push(L.geoJSON(franceGeoJson, {
        style: () => bgStyle,
        pane: 'territory-bg-pane',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onEachFeature: (feature: any, fl: any) => {
          const tid = deptToTerritory[feature.properties.code];
          if (!tid) return;
          fl.on('click', () => onTerritoryChangeRef.current(tid === currentTerritoryRef.current ? '' : tid));
          fl.on('mouseover', () => fl.setStyle({ fillOpacity: 0.12, color: TERRITORY_STYLE[tid]?.border ?? '#888', opacity: 0.6 }));
          fl.on('mouseout', () => fl.setStyle(bgStyle));
        },
      }));
    }
    if (worldGeoJson) {
      const relevantNames = new Set(Object.values(TERRITORY_COUNTRY_NAME));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      layers.push(L.geoJSON(worldGeoJson, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter: (f: any) => relevantNames.has(f.properties.name),
        style: () => bgStyle,
        pane: 'territory-bg-pane',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onEachFeature: (feature: any, fl: any) => {
          const tid = countryToTerritory[feature.properties.name];
          if (!tid) return;
          fl.on('click', () => onTerritoryChangeRef.current(tid === currentTerritoryRef.current ? '' : tid));
          fl.on('mouseover', () => fl.setStyle({ fillOpacity: 0.12, color: TERRITORY_STYLE[tid]?.border ?? '#888', opacity: 0.6 }));
          fl.on('mouseout', () => fl.setStyle(bgStyle));
        },
      }));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group = (L as any).layerGroup(layers);
    group.addTo(map);
    bgLayerRef.current = group;
  }, [ready, franceGeoJson, worldGeoJson, deptToTerritory, countryToTerritory]);

  // Update territory GeoJSON highlight layer
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;
    if (geoLayerRef.current) { (geoLayerRef.current as any).remove(); geoLayerRef.current = null; }
    if (!territoryFilter) return;

    const style = TERRITORY_STYLE[territoryFilter] ?? { fill: '#c0d8f0', border: '#4488bb' };
    const layerStyle = () => ({ fillColor: style.fill, fillOpacity: 0.25, color: style.border, weight: 1, opacity: 0.3, pane: 'territory-pane' });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let geoData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let filterFn: (f: any) => boolean;

    if (FRENCH_TERRITORIES.has(territoryFilter)) {
      if (!franceGeoJson) return;
      const activeCodes = new Set(departments.filter(d => d.territoryId === territoryFilter).map(d => d.code));
      geoData = franceGeoJson;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn = (f: any) => activeCodes.has(f.properties.code);
    } else {
      if (!worldGeoJson) return;
      const countryName = TERRITORY_COUNTRY_NAME[territoryFilter];
      if (!countryName) return;
      geoData = worldGeoJson;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn = (f: any) => f.properties.name === countryName;
    }

    const layer = L.geoJSON(geoData, { filter: filterFn, style: layerStyle, interactive: false, pane: 'territory-pane' });
    layer.addTo(map);
    geoLayerRef.current = layer;
  }, [ready, franceGeoJson, worldGeoJson, territoryFilter, departments]);

  // Contracts layer
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;
    if (!showContracts) {
      for (const c of contractCirclesRef.current) { try { c.remove(); } catch { /* noop */ } }
      contractCirclesRef.current = [];
      return;
    }
    drawContractCircles(L, map, dsps, cityById, selectedDspId, dspScenarioCounts, (id: string) => onSelectRef.current(id), contractCirclesRef);
  }, [ready, dsps, cityById, selectedDspId, dspScenarioCounts, showContracts]);

  // Networks layer
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;
    if (!showNetworks) {
      for (const c of networkCirclesRef.current) { try { c.remove(); } catch { /* noop */ } }
      networkCirclesRef.current = [];
      return;
    }
    drawNetworkCircles(L, map, networks, cityById, selectedNetworkId, (id: string) => onSelectNetworkRef.current(id), networkCirclesRef);
  }, [ready, networks, cityById, selectedNetworkId, showNetworks]);

  useEffect(() => {
    if (!ready || !mapRef.current || !showNetworks || didAutoFitNetworksRef.current) return;
    const coords = networks
      .filter(net => Number.isFinite(net.lat) && Number.isFinite(net.lng))
      .map(net => [net.lat, net.lng] as [number, number]);
    if (!coords.length) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any).L;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds.pad(0.18), { animate: false, maxZoom: 7 });
    didAutoFitNetworksRef.current = true;
  }, [ready, networks, showNetworks]);

  if (!ready) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><NJSpinner /></div>;
  }
  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawContractCircles(
  L: any,
  map: any,
  dsps: DSP[],
  cityById: Record<string, City>,
  selectedDspId: string | null,
  dspScenarioCounts: Record<string, DspScenarioCounts> | undefined,
  onSelect: (id: string) => void,
  circlesRef: React.MutableRefObject<any[]>,
) {
  for (const c of circlesRef.current) { try { c.remove(); } catch { /* noop */ } }
  circlesRef.current = [];

  for (const dsp of dsps) {
    const city = cityById[dsp.cityId];
    if (!city) continue;
    const coords = CITY_COORDS[city.name];
    if (!coords) continue;
    const isSelected = dsp.id === selectedDspId;
    const color = TYPE_COLOR[dsp.type] ?? '#999';
    const circle = L.circleMarker(coords, {
      radius: isSelected ? 22 : 13,
      color: isSelected ? '#fff' : color,
      weight: isSelected ? 3 : 1.5,
      fillColor: color,
      fillOpacity: isSelected ? 0.92 : 0.55,
    });
    circle.addTo(map);
    const sc = dspScenarioCounts?.[dsp.id] ?? { active: 0, archived: 0, reference: 0, studiesActive: 0, studiesArchived: 0 };
    circle.bindTooltip(
      `<div style="font-family:inherit;min-width:180px">
        <div style="font-weight:700;font-size:13px;margin-bottom:2px">${dsp.name}</div>
        <div style="font-size:11px;color:#616161;margin-bottom:8px">${dsp.identifiantSNCU} \u00b7 ${city.name} \u00b7 ${TYPE_LABEL[dsp.type]}</div>
        <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;font-size:11px">
          <div style="background:#f0f4ff;border-radius:6px;padding:6px 8px;display:flex;flex-direction:column;gap:5px">
            <div style="color:#0052CC;font-weight:700">\u00c9tudes</div>
            <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
              <span style="color:#0052CC">Actives</span>
              <span style="font-weight:700;font-size:15px;color:#0052CC">${sc.studiesActive}</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;border-top:1px solid #d9e6ff;padding-top:4px">
              <span style="color:#616161">Archiv\u00e9e${sc.studiesArchived > 1 ? 's' : ''}</span>
              <span style="font-weight:700;color:#616161">${sc.studiesArchived}</span>
            </div>
          </div>
          <div style="background:#e8f5e9;border-radius:6px;padding:6px 8px;display:flex;flex-direction:column;gap:5px">
            <div style="color:#2e7d32;font-weight:700">R\u00e9seaux</div>
            <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
              <span style="color:#2e7d32">Actifs</span>
              <span style="font-weight:700;font-size:15px;color:#2e7d32">${sc.active}</span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;border-top:1px solid #d3ead4;padding-top:4px">
              <span style="color:#616161">Archiv\u00e9${sc.archived > 1 ? 's' : ''}</span>
              <span style="font-weight:700;color:#616161">${sc.archived}</span>
            </div>
          </div>
        </div>
      </div>`,
      { direction: 'top', className: 'nemo-map-tooltip' },
    );
    circle.on('click', () => onSelect(dsp.id));
    circlesRef.current.push(circle);
  }
}



// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawNetworkCircles(
  L: any,
  map: any,
  networks: Network[],
  cityById: Record<string, City>,
  selectedNetworkId: string | null,
  onSelect: (id: string) => void,
  circlesRef: React.MutableRefObject<any[]>,
) {
  for (const c of circlesRef.current) { try { c.remove(); } catch { /* noop */ } }
  circlesRef.current = [];
  if (!networks.length) return;

  let added = 0;
  let fallbackToCity = 0;
  let skipped = 0;

  // Render each network with demo coordinates (deterministic jitter around city center)
  for (const net of networks) {
    const city = cityById[net.cityId];
    const cityName = city?.name ?? '';
    const color = TYPE_COLOR[net.type] ?? '#999';
    const cityCoords = city ? CITY_COORDS[city.name] : undefined;
    const hasOwnCoords = Number.isFinite(net.lat) && Number.isFinite(net.lng);
    const coords = hasOwnCoords
      ? [net.lat, net.lng]
      : (cityCoords ? getMockNetworkCoords(net.id, cityCoords) : null);

    if (!coords) {
      skipped++;
      continue;
    }
    if (!hasOwnCoords) fallbackToCity++;

    // Radius proportional to network length (min 8, max 20) so the mock layer is easy to inspect.
    const baseRadius = Math.max(8, Math.min(20, 8 + (net.length / 14)));
    const isSelected = selectedNetworkId === net.id;
    const radius = isSelected ? baseRadius + 3 : baseRadius;

    try {
      const circle = L.circleMarker(coords, {
        radius,
        color: isSelected ? '#111111' : '#ffffff',
        weight: isSelected ? 3 : 2,
        fillColor: color,
        fillOpacity: isSelected ? 1 : 0.9,
      });
      circle.addTo(map);

      circle.bindTooltip(
        `<div style="font-family:inherit;min-width:180px">
          <div style="font-weight:700;font-size:13px;margin-bottom:2px">${net.name}</div>
          <div style="font-size:11px;color:#616161;margin-bottom:6px">${cityName} \u2022 ${TYPE_LABEL[net.type] ?? net.type}</div>
          <div style="font-size:11px;display:grid;grid-template-columns:auto 1fr;gap:2px 8px">
            <span style="color:#616161">Longueur</span><span style="font-weight:600">${net.length} km</span>
            <span style="color:#616161">Abonn\u00e9s</span><span style="font-weight:600">${net.subscribers}</span>
            <span style="color:#616161">Temp\u00e9rature</span><span style="font-weight:600">${net.temperature}\u00b0C</span>
            <span style="color:#616161">Coordonn\u00e9es</span><span style="font-weight:600">${net.lat.toFixed(4)}, ${net.lng.toFixed(4)}</span>
            <span style="color:#616161">Exploitant</span><span style="font-weight:600">${net.owner}</span>
          </div>
        </div>`,
        { direction: 'top', className: 'nemo-map-tooltip' },
      );
      circle.on('click', () => onSelect(net.id));
      circlesRef.current.push(circle);
      added++;
    } catch {
      skipped++;
    }
  }

  // Debug aid: helps diagnose data/coordinate issues quickly in browser console
  // eslint-disable-next-line no-console
  console.info('[NetworkMap] markers', { total: networks.length, added, fallbackToCity, skipped });
}

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) >>> 0;
  }
  return h;
}

function getMockNetworkCoords(networkId: string, cityCoords: [number, number]): [number, number] {
  const seed = hashString(networkId);
  // Radius in degrees (~0.8km to ~5.5km depending on latitude)
  const radius = 0.008 + (seed % 12) * 0.002;
  const angle = (seed % 360) * (Math.PI / 180);
  const lat = cityCoords[0] + radius * Math.sin(angle);
  const lng = cityCoords[1] + radius * Math.cos(angle);
  return [lat, lng];
}

// ── Networks Legend overlay ────────────────────────────────────────────────────
function NetworksLegend() {
  return (
    <div style={{ zIndex: 1000, background: 'white', border: '1px solid #e0e0e0', borderRadius: 6, padding: '8px 12px', fontSize: 11, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11 }}>Type de réseau</div>
      {Object.entries(TYPE_LABEL).map(([type, label]) => (
        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: '50%', background: TYPE_COLOR[type], flexShrink: 0 }} />
          {label}
        </div>
      ))}
      <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 8, paddingTop: 8 }}>
        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11 }}>Taille = longueur réseau</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#999', flexShrink: 0 }} />
          <span>Court (&lt; 40 km)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', background: '#999', flexShrink: 0 }} />
          <span>Long (&gt; 90 km)</span>
        </div>
      </div>
    </div>
  );
}

// ── Map legend overlay ─────────────────────────────────────────────────────────
function MapLegend() {
  return (
    <div style={{ position: 'absolute', bottom: 28, right: 12, zIndex: 1000, background: 'white', border: '1px solid #e0e0e0', borderRadius: 6, padding: '8px 12px', fontSize: 11, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11 }}>Type de réseau</div>
      {Object.entries(TYPE_LABEL).map(([type, label]) => (
        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: '50%', background: TYPE_COLOR[type], flexShrink: 0 }} />
          {label}
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function NetworkLifecycleManagerList() {
  const { data, isLoading, error } = useExplorerData();
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [mapSelectedDspId, setMapSelectedDspId] = useState<string | null>(null);
  const [mapSelectedNetworkId, setMapSelectedNetworkId] = useState<string | null>(null);
  const [isPrimaryMenuFolded, setIsPrimaryMenuFolded] = useState(true);
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [viewportFilterActive, setViewportFilterActive] = useState(false);
  const [showContracts, setShowContracts] = useState(true);
  const [showNetworks, setShowNetworks] = useState(false);
  const [tableNetworkScope, setTableNetworkScope] = useState<{ dspIds: Set<string> | null; orphanNetworkIds: Set<string> | null }>({ dspIds: null, orphanNetworkIds: null });
  const AUTO_FILTER_ZOOM = 7;

  const cityById = useMemo(() => Object.fromEntries((data?.cities ?? []).map(c => [c.id, c])), [data]);
  const networkById = useMemo(() => Object.fromEntries((data?.networks ?? []).map(n => [n.id, n])), [data]);

  const handleTerritoryChange = useCallback((id: string) => {
    setMapSelectedDspId(null);
    setTerritoryFilter(id);
    setViewportFilterActive(false);
  }, []);

  const handleZoomChange = useCallback((zoom: number) => {
    setViewportFilterActive(zoom >= AUTO_FILTER_ZOOM);
    if (zoom >= AUTO_FILTER_ZOOM) setTerritoryFilter('');
  }, []);

  const dspScenarioCounts = useMemo<Record<string, DspScenarioCounts>>(() => {
    if (!data) return {};
    const counts: Record<string, DspScenarioCounts> = {};
    for (const sc of data.scenarios) {
      if (!counts[sc.dspId]) counts[sc.dspId] = { active: 0, archived: 0, reference: 0, studiesActive: 0, studiesArchived: 0 };
      counts[sc.dspId][sc.status as 'active' | 'archived' | 'reference']++;
    }
    for (const dsp of data.dsps) {
      const dspStudies = data.studies.filter(s => s.dspId === dsp.id);
      let activeStudies = 0;
      let archivedStudies = 0;
      for (const study of dspStudies) {
        const studyScenarios = data.scenarios.filter(sc => sc.studyId === study.id && sc.status !== 'reference');
        if (studyScenarios.length > 0 && studyScenarios.every(sc => sc.status === 'archived')) archivedStudies++;
        if (studyScenarios.some(sc => sc.status === 'active')) activeStudies++;
      }
      if (!counts[dsp.id]) counts[dsp.id] = { active: 0, archived: 0, reference: 0, studiesActive: 0, studiesArchived: 0 };
      counts[dsp.id].studiesActive = activeStudies;
      counts[dsp.id].studiesArchived = archivedStudies;
    }
    return counts;
  }, [data]);

  // City IDs whose coordinates fall within the current map viewport
  const visibleCityIds = useMemo<Set<string> | null>(() => {
    if (!mapBounds || !data) return null;
    const { north, south, east, west } = mapBounds;
    const ids = new Set<string>();
    for (const city of data.cities) {
      const coords = CITY_COORDS[city.name];
      if (!coords) continue;
      const [lat, lng] = coords;
      if (lat >= south && lat <= north && lng >= west && lng <= east) ids.add(city.id);
    }
    return ids;
  }, [mapBounds, data]);

  const listDspIds = useMemo<Set<string> | null>(() => {
    if (!data) return null;
    if (mapSelectedDspId) return new Set([mapSelectedDspId]);
    if (territoryFilter) {
      return new Set(data.dsps.filter(d => cityById[d.cityId]?.territoryId === territoryFilter).map(d => d.id));
    }
    if (viewportFilterActive && visibleCityIds) {
      return new Set(data.dsps.filter(d => visibleCityIds.has(d.cityId)).map(d => d.id));
    }
    return null;
  }, [mapSelectedDspId, territoryFilter, viewportFilterActive, visibleCityIds, data, cityById]);

  const listFilterLabel = useMemo(() => {
    if (mapSelectedDspId) return data?.dsps.find(d => d.id === mapSelectedDspId)?.name ?? 'DSP s\u00e9lectionn\u00e9e';
    if (territoryFilter) return data?.territories.find(t => t.id === territoryFilter)?.name ?? territoryFilter;
    if (viewportFilterActive) return 'Vue actuelle';
    return undefined;
  }, [mapSelectedDspId, territoryFilter, viewportFilterActive, data]);

  const mapFilteredNetworkIds = useMemo<Set<string> | null>(() => {
    if (!data) return null;
    if (mapSelectedNetworkId) return new Set([mapSelectedNetworkId]);
    if (mapSelectedDspId) {
      return new Set((data.networks ?? []).filter((n) => n.dspId === mapSelectedDspId).map((n) => n.id));
    }
    if (territoryFilter) {
      return new Set(
        (data.networks ?? [])
          .filter((n) => cityById[n.cityId]?.territoryId === territoryFilter)
          .map((n) => n.id),
      );
    }
    if (viewportFilterActive && visibleCityIds) {
      return new Set((data.networks ?? []).filter((n) => visibleCityIds.has(n.cityId)).map((n) => n.id));
    }
    return null;
  }, [data, mapSelectedNetworkId, mapSelectedDspId, territoryFilter, viewportFilterActive, visibleCityIds, cityById]);

  const tableFilteredNetworkIds = useMemo<Set<string> | null>(() => {
    if (!data || !tableNetworkScope.dspIds) return null;
    const ids = new Set<string>();
    for (const network of data.networks ?? []) {
      if (network.dspId && tableNetworkScope.dspIds.has(network.dspId)) {
        ids.add(network.id);
        continue;
      }
      if (!network.dspId && !network.studyId && tableNetworkScope.orphanNetworkIds?.has(network.id)) {
        ids.add(network.id);
      }
    }
    return ids;
  }, [data, tableNetworkScope]);

  const filteredNetworks = useMemo(() => {
    if (!data) return [];
    return (data.networks ?? []).filter((network) => {
      const matchesMap = !mapFilteredNetworkIds || mapFilteredNetworkIds.has(network.id);
      const matchesTable = !tableFilteredNetworkIds || tableFilteredNetworkIds.has(network.id);
      return matchesMap && matchesTable;
    });
  }, [data, mapFilteredNetworkIds, tableFilteredNetworkIds]);

  const filteredDsps = useMemo(() => {
    if (!data) return [];
    if (!territoryFilter) return data.dsps;
    return data.dsps.filter(dsp => cityById[dsp.cityId]?.territoryId === territoryFilter);
  }, [data, cityById, territoryFilter]);

  const selectedDspId = useMemo(() => {
    if (!selection || !data) return null;
    if (selection.kind === 'dsp') return selection.id;
    if (selection.kind === 'study') return data.studies.find(s => s.id === selection.id)?.dspId ?? null;
    if (selection.kind === 'scenario') return data.scenarios.find(s => s.id === selection.id)?.dspId ?? null;
    return null;
  }, [selection, data]);

  const handleSelectDsp = useCallback((id: string) => {
    setMapSelectedNetworkId(null);
    setMapSelectedDspId(prev => {
      const next = prev === id ? null : id;
      if (next) setSelection({ kind: 'dsp', id: next });
      else setSelection(null);
      return next;
    });
  }, []);

  const handleSelectNetwork = useCallback((id: string) => {
    setMapSelectedNetworkId(prev => {
      const next = prev === id ? null : id;
      if (!next) {
        setMapSelectedDspId(null);
        setSelection(null);
        return null;
      }
      const network = networkById[next];
      setMapSelectedDspId(network?.dspId ?? null);
      if (network?.dspId) setSelection({ kind: 'dsp', id: network.dspId });
      return next;
    });
  }, [networkById]);

  const toggleContracts = useCallback(() => {
    if (showContracts && !showNetworks) return;
    if (showContracts) setMapSelectedDspId(null);
    if (showContracts) setMapSelectedNetworkId(null);
    setShowContracts(v => !v);
  }, [showContracts, showNetworks]);

  const toggleNetworks = useCallback(() => {
    if (showNetworks && !showContracts) return;
    if (showNetworks) setMapSelectedNetworkId(null);
    setShowNetworks(v => !v);
  }, [showContracts, showNetworks]);

  if (isLoading) return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><NJSpinner /></div>
    </PrototypeStudioLayout>
  );
  if (error || !data) return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ padding: 24 }}><NJInlineMessage variant="danger" title="Erreur">Impossible de charger les données.</NJInlineMessage></div>
    </PrototypeStudioLayout>
  );

  return (
    <PrototypeStudioLayout title={meta.title}>
      <style>{css}</style>
      <NJToastContainer />

      {/* ── Root flex-row (Pattern 4a) ── */}
      <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', overflow: 'hidden' }}>

        {/* ── Sidebar primaire ── */}
        <div style={{ width: isPrimaryMenuFolded ? PRIMARY_SIDEBAR_WIDTH_FOLDED : PRIMARY_SIDEBAR_WIDTH_OPEN, flexShrink: 0, height: '100%', overflow: 'hidden', transition: 'width 180ms ease' }}>
          <NJSidebarDeprecated isFolded={isPrimaryMenuFolded} className="nemo-primary-sidebar">
            <NJSidebarBrandDeprecated
              imageSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='24' viewBox='0 0 80 24'%3E%3Ctext y='18' font-size='18' font-family='Lato,sans-serif' font-weight='700' fill='%230052CC'%3ENeMo%3C/text%3E%3C/svg%3E"
              imageFoldedSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3E%3Crect x='.25' y='.25' width='29.5' height='29.5' rx='5.75' fill='white' stroke='%237FADEF' stroke-width='.5'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M21.472 16.7C21.472 18.07 21.045 19.34 20.318 20.38H9.747C9.02 19.34 8.593 18.07 8.593 16.7c0-3.558 2.883-6.44 6.44-6.44 1.343 0 2.591.412 3.623 1.116.87-.302 1.882-.636 3.053-1.004C19.033 8.603 16.662 7.5 15.033 7.5 9.952 7.5 5.833 11.62 5.833 16.7c0 2.508 1.003 4.782 2.63 6.442h13.14C23.229 21.482 24.232 19.21 24.232 16.7c0-1.136-.206-2.224-.582-3.228-.855.786-1.614 1.466-2.285 2.051.07.382.107.775.107 1.177zm-8.08.546c.508.88 1.623 1.187 2.49.686.868-.5 2.912-2.212 6.934-6.128-5.402 1.525-7.906 2.44-8.773 2.941-.868.5-1.159 1.62-.651 2.5z' fill='url(%23g)'/%3E%3Cdefs%3E%3ClinearGradient id='g' x1='-2.85' y1='15.16' x2='12' y2='32.62' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2300AAFF'/%3E%3Cstop offset='.97' stop-color='%2323D2B5'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
            />
            <NJSidebarMenuDeprecated isFolded>
              <>
                {PRIMARY_NAV.map(item => (
                  <li key={item.id} className={`nj-list-deprecated__item nj-list-deprecated__item--clickable${item.active ? ' nj-list-deprecated__item--active' : ''}`} style={{ overflow: 'hidden' }}>
                    <button type="button" aria-label={item.label} style={{ overflow: 'hidden' }}>
                      {item.ai
                        ? <span className="nj-list-deprecated__item-icon"><NJIcon name="engie_ai" size="md" variant="ai" /></span>
                        : <span className="material-icons nj-icon-material nj-icon-material--md nj-icon-material--grey nj-list-deprecated__item-icon" aria-hidden="true">{item.icon}</span>
                      }
                      <div className={`nj-list-deprecated__item-content${isPrimaryMenuFolded ? ' hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>{item.label}</div>
                    </button>
                  </li>
                ))}
              </>
            </NJSidebarMenuDeprecated>
            <NJSidebarMenuDeprecated isFolded isFooterNav>
              <>
                <NJSidebarDividerDeprecated />
                {PRIMARY_FOOTER.map(item => (
                  <li key={item.id} className="nj-list-deprecated__item nj-list-deprecated__item--clickable" style={{ overflow: 'hidden' }}>
                    <button type="button" aria-label={item.label} style={{ overflow: 'hidden' }}>
                      <span className="material-icons nj-icon-material nj-icon-material--md nj-icon-material--grey nj-list-deprecated__item-icon" aria-hidden="true">{item.icon}</span>
                      <div className={`nj-list-deprecated__item-content${isPrimaryMenuFolded ? ' hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>{item.label}</div>
                    </button>
                  </li>
                ))}
              </>
            </NJSidebarMenuDeprecated>
            <NJSidebarCloseDeprecated onClick={() => setIsPrimaryMenuFolded(f => !f)}>Réduire</NJSidebarCloseDeprecated>
          </NJSidebarDeprecated>
        </div>

        {/* ── Colonne droite ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }}>

          {/* Header */}
          <header style={{ height: 48, flexShrink: 0, borderBottom: '1px solid var(--nj-core-color-grey-300)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', background: 'var(--nj-semantic-color-background-neutral-primary-default)', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--nj-semantic-color-text-brand-primary-default)' }}>view_list</span>
              <NJHeading scale="sm" as="h1">Réseaux — Vue Liste</NJHeading>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <NJIconButton icon="search" label="Rechercher" variant="tertiary" />
              <NJIconButton icon="notifications_none" label="Notifications" variant="tertiary" />
              <div style={{ width: 1, height: 24, background: 'var(--nj-core-color-grey-300)', margin: '0 4px' }} />
              <button type="button" style={{ fontSize: 13, border: 'none', cursor: 'pointer', color: 'var(--nj-semantic-color-text-tertiary-default)', background: 'transparent' }}>FR</button>
              <NJIconButton icon="keyboard_arrow_down" label="Langue" variant="tertiary" scale="sm" />
            </div>
          </header>

          {/* Tab bar */}
          <div style={{ flexShrink: 0, borderBottom: '1px solid var(--nj-semantic-color-border-neutral-default)', background: 'var(--nj-semantic-color-background-neutral-primary-default)', paddingLeft: 16 }}>
            <NJTabs
              label="Navigation"
              activeTab={activeTab}
              density="compact"
              onClickTabItem={(id) => setActiveTab(id as 'list' | 'stats')}
            >
              <NJTab id="list" label="Liste" ariaControls="panel-list" />
              <NJTab id="stats" label="Statistiques" ariaControls="panel-stats" />
            </NJTabs>
          </div>

          {/* Stats panel */}
          {activeTab === 'stats' && (
            <div id="panel-stats" style={{ flex: 1, overflow: 'hidden' }}>
              <StatsTab />
            </div>
          )}

          {/* Main */}
          {activeTab === 'list' && (
          <main id="panel-list" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            {/* ── List drill-down (flex) ── */}
            <DspListView
              data={data}
              selection={selection}
              onNavigate={setSelection}
              filteredDspIds={listDspIds}
              filterLabel={listFilterLabel}
              onClearFilter={() => { setMapSelectedDspId(null); setMapSelectedNetworkId(null); setTerritoryFilter(''); setViewportFilterActive(false); }}
              onNetworkScopeChange={setTableNetworkScope}
            />
            {/* ── Map on the right ── */}
            <div style={{ width: 580, flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', background: 'var(--nj-semantic-color-background-neutral-primary-default)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: 'var(--nj-semantic-color-text-brand-primary-default)' }}>map</span>
                <NJHeading scale="sm" as="h2">Carte</NJHeading>
                <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'inline-flex', background: 'var(--nj-semantic-color-background-neutral-secondary-default)', border: '1px solid var(--nj-semantic-color-border-neutral-secondary-default)', borderRadius: 4, padding: 2, gap: 2 }}>
                  <NJTooltip text="Afficher/Masquer les contrats" placement="bottom" hasArrow>
                    <button
                      onClick={toggleContracts}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, border: 'none', borderRadius: 3, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', transition: 'background 120ms', background: showContracts ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent', color: showContracts ? 'var(--nj-semantic-color-text-brand-primary-default)' : 'var(--nj-semantic-color-text-secondary-default)', opacity: showContracts || showNetworks ? 1 : 0.6 }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 13 }}>hub</span>
                      Contrats
                    </button>
                  </NJTooltip>
                  <NJTooltip text="Afficher/Masquer les réseaux" placement="bottom" hasArrow>
                    <button
                      onClick={toggleNetworks}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, border: 'none', borderRadius: 3, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', transition: 'background 120ms', background: showNetworks ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent', color: showNetworks ? 'var(--nj-semantic-color-text-brand-primary-default)' : 'var(--nj-semantic-color-text-secondary-default)', opacity: showContracts || showNetworks ? 1 : 0.6 }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 13 }}>cable</span>
                      Réseaux
                    </button>
                  </NJTooltip>
                </div>
                <NJTooltip text={viewportFilterActive ? 'Cliquer pour désactiver le filtre par vue' : 'Zoom ≥ 7 pour activer automatiquement'} placement="bottom" hasArrow>
                  <button
                    onClick={() => { setViewportFilterActive(f => !f); setTerritoryFilter(''); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, border: '1px solid', borderRadius: 20, padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit', transition: 'background 120ms', background: viewportFilterActive ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent', color: viewportFilterActive ? 'var(--nj-semantic-color-text-brand-primary-default)' : 'var(--nj-semantic-color-text-secondary-default)', borderColor: viewportFilterActive ? 'var(--nj-semantic-color-border-brand-primary-default)' : 'var(--nj-semantic-color-border-neutral-secondary-default)' }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 13 }}>my_location</span>
                    {viewportFilterActive ? 'Filtre actif' : 'Auto (zoom ≥ 7)'}
                  </button>
                </NJTooltip>
              </div>
              </div>
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                <NetworkMap dsps={filteredDsps} networks={filteredNetworks} cityById={cityById} selectedDspId={selectedDspId} selectedNetworkId={mapSelectedNetworkId} onSelectDsp={handleSelectDsp} onSelectNetwork={handleSelectNetwork} territoryFilter={territoryFilter} departments={data.departments} onTerritoryChange={handleTerritoryChange} onBoundsChange={setMapBounds} onZoomChange={handleZoomChange} dspScenarioCounts={dspScenarioCounts} showContracts={showContracts} showNetworks={showNetworks} />
                {showContracts && <MapLegend />}
                {showNetworks && <div style={{ position: 'absolute', top: 12, right: 12 }}><NetworksLegend /></div>}
              </div>
            </div>
          </main>
          )}

          {/* Footer ENGIE */}
          <footer role="contentinfo" style={{ minHeight: 48, flexShrink: 0, display: 'flex', alignItems: 'center', borderTop: '1px solid var(--nj-core-color-grey-300)', background: 'var(--nj-semantic-color-background-neutral-secondary-default)', padding: '0 24px', gap: 24 }}>
            <svg width="51" height="18" viewBox="0 0 51 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="ENGIE" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M50.1383 7.19874C43.4203 2.71327 34.6567 0 25.0699 0C15.4831 0 6.71799 2.71327 0 7.19874C6.68185 4.97722 14.4933 3.59937 25.0699 3.59937C35.6465 3.59937 43.458 4.97722 50.1383 7.19874Z" fill="#00AAFF"/>
              <path d="M13.418 9.53H10.5V18H13.418V9.53Z" fill="#009DE0"/>
              <path d="M10.5 9.53H7.582V18H10.5V9.53Z" fill="#009DE0"/>
            </svg>
            <div style={{ flex: 1 }} />
            <a href="#" onClick={e => e.preventDefault()} className="nj-link nj-link--contextual" style={{ fontSize: 11 }}>Contacter le support</a>
            <a href="#" onClick={e => e.preventDefault()} className="nj-link nj-link--contextual" style={{ fontSize: 11 }}>Politique de confidentialité</a>
          </footer>

        </div>
      </div>
    </PrototypeStudioLayout>
  );
}
